import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import useMap from "@/lib/map/useMap";
import {
    PLANTING_SUMMARY_IMAGE_LAYER,
    PLANTING_SUMMARY_SOURCE,
} from "@/lib/map/layers";
import {
    removeLayersIfExists,
    removeSourcesIfExists,
    invertedTurfBboxToFourCornerCoordinates,
    Coordinates,
} from "@/lib/utils/map";
import { CropConfiguration } from "@/types/cropConfiguration";
import { ManagementZones } from "@/types/managementZones";
import { PlantingOperation } from "@/types/operations";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { number, object, date, array, string, mixed, InferType } from "yup";
import {
    Button,
    Divider,
    getKeyValue,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faFill,
    faSave,
    faPlusCircle,
} from "@fortawesome/free-solid-svg-icons";
import TextInput from "@/components/inputs/TextInput";
import TextAreaInput from "@/components/inputs/TextAreaInput";
import ShapefileUpload from "./ShapefileUpload";
import { fileInputSchema, skipTableValidationField } from "@/lib/form/operation";
import { FileProperty } from "@/types/file";
import { useTranslation } from "react-i18next";
import PlantingCropDetailsForm from "./PlantingCropDetailsForm";
import { useI18n } from "@/lib/i18n/useI18n";
import {
    hasFileSource,
    hasInputRates,
    inputRatesToPlantingTableRows,
} from "@/lib/utils/operations";

const schema = object().shape({
    _skipTableValidation: skipTableValidationField,
    start_date: date().required().label("Start Date"),
    crop_configuration: number().required().label("Crop"),
    crm: number()
        .nullable()
        .transform((value, originalValue) =>
            originalValue === "" ? null : value,
        )
        .integer(),
    gdd_silk: number()
        .nullable()
        .transform((value, originalValue) =>
            originalValue === "" ? null : value,
        )
        .integer(),
    gdd_mature: number()
        .nullable()
        .transform((value, originalValue) =>
            originalValue === "" ? null : value,
        )
        .integer(),
    hybrid: string().nullable(),
    maturity_level: string().nullable().oneOf(["early", "medium", "late"]),
    rice_type: string().nullable().oneOf(["american", "japanese"]),
    sugarcane_type: string().nullable().oneOf(["high_fiber", "high_sucrose"]),
    soybean_maturity_group: number().nullable().min(1).max(11),
    note: string().nullable(),
    table: array().when("_skipTableValidation", {
        is: (skip: boolean) => !!skip,
        then: (schema) => schema.notRequired().nullable(),
        otherwise: () =>
            array().of(
                object().shape({
                    zone: number().nullable().label("Zone"),
                    zone_id: number().nullable().label("Zone"),
                    depth: number()
                        .typeError("Not a valid number")
                        .min(0)
                        .label("Depth"),
                    seeding_rate: number()
                        .typeError("Required")
                        .required("Required")
                        .label("Seeding Rate")
                        .min(0),
                }),
            ),
    }),
    batch_fill: mixed(),
    properties: fileInputSchema,
});

type PlantingOperationFormType = InferType<typeof schema>;

function PlantingOperationForm({
    existingPlantingOperationId,
}: {
    existingPlantingOperationId?: number;
}) {
    const { formatUnit } = useI18n();
    const { t } = useTranslation(["operations", "messages"]);
    const COLUMNS = useMemo(
        () => [
            { key: "zone", label: t("zone") },
            {
                key: "depth",
                label: `${t("seedingDepth")} (${formatUnit("in", "cm")})`,
            },
            {
                key: "seeding_rate",
                label: `${t("seedingRate")} (seeds/${formatUnit("ac", "ha")})`,
            },
        ],
        [t, formatUnit],
    );
    const { fieldId, fieldQuery, cropCycleQueryV2, fieldBbox } =
        useFieldDashboard() ?? {};
    const { useGetRequest, useMutateRequest } = useRequest();
    const { map, mapLoaded } = useMap();
    const navigate = useNavigate();

    const methods = useForm<PlantingOperationFormType>({
        resolver: yupResolver(schema),
        defaultValues: {
            _skipTableValidation: false,
        },
    });

    const { handleSubmit, trigger, setValue, getValues, watch } = methods;

    const [crop_configuration, batch_fill] = watch([
        "crop_configuration",
        "batch_fill",
    ]);

    const cropsQuery = useGetRequest<CropConfiguration[]>({
        url: "crops/",
    });

    const fileProperties = useGetRequest<FileProperty[]>({
        url: "planting_operations/file_properties/",
    });

    const managementZonesQuery = useGetRequest<ManagementZones[]>({
        url: `fields/${fieldId}/management_zones/`,
    });
    const preferredManagementZones = useMemo(() => {
        return managementZonesQuery.data?.find((zone) => zone.is_primary);
    }, [managementZonesQuery.data]);

    const existingPlantingOperationQuery = useGetRequest<PlantingOperation>({
        url: `planting_operations/${existingPlantingOperationId}/`,
        enabled: !!existingPlantingOperationId,
    });
    const [initializedPlantingOperationId, setInitializedPlantingOperationId] =
        useState<number>();
    const isInitialized =
        !existingPlantingOperationId ||
        initializedPlantingOperationId === existingPlantingOperationId;

    const mapImageQuery = useGetRequest<Blob>({
        url: `planting_operations/${existingPlantingOperationId}/map_display_image/`,
        responseType: "blob",
        enabled: !!existingPlantingOperationId,
    });

    useEffect(() => {
        if (
            !map ||
            !mapLoaded ||
            !fieldBbox ||
            !existingPlantingOperationId ||
            !mapImageQuery.data
        ) return;

        const imageUrl = window.URL.createObjectURL(mapImageQuery.data);
        const bbox = existingPlantingOperationQuery.data?.bounds
            ? (invertedTurfBboxToFourCornerCoordinates(
                existingPlantingOperationQuery.data.bounds,
            ) as Coordinates)
            : undefined;

        map.addSource(PLANTING_SUMMARY_SOURCE, {
            type: "image",
            url: imageUrl,
            coordinates: bbox || fieldBbox,
        });
        map.addLayer(PLANTING_SUMMARY_IMAGE_LAYER);

        return () => {
            removeLayersIfExists(map, [PLANTING_SUMMARY_IMAGE_LAYER.id]);
            removeSourcesIfExists(map, [PLANTING_SUMMARY_SOURCE]);
            window.URL.revokeObjectURL(imageUrl);
        };
    }, [
        map,
        mapLoaded,
        fieldBbox,
        existingPlantingOperationId,
        mapImageQuery.data,
        existingPlantingOperationQuery.data?.bounds,
    ]);

    const hasStoredInputRates = hasInputRates(
        existingPlantingOperationQuery.data?.input_rates,
    );

    const existingPlantingOperationRatesQuery = useGetRequest<
        | {
            id: number;
            zone_number: number;
            mean_1: number;
            mean_2: number;
        }[]
        | { rate_1: number; rate_2: number }
    >({
        url: `planting_operations/${existingPlantingOperationId}/rates/`,
        params: {
            by: "zone",
        },
        enabled: !!existingPlantingOperationId && !hasStoredInputRates,
    });

    const savePlantingOperationMutation = useMutateRequest<{ id: number }>({
        successMessage: t("success.planting_operation_saved"),
        errorMessage: t("error.failed_to_save_planting_operation"),
    });

    const uploadOperation = useMutateRequest({
        errorMessage: t("error.failed_to_upload_operation"),
        successMessage: t("success.operation_uploaded_successfully"),
    });

    const { fields } = useFieldArray<PlantingOperationFormType, "table">({
        control: methods.control,
        name: "table",
    });

    const [selectedFile, setSelectedFile] = useState<File | undefined | null>(
        undefined,
    );

    const selectedCrop = useMemo(() => {
        return cropsQuery.data?.find((crop) => crop.id == crop_configuration);
    }, [cropsQuery.data, crop_configuration]);

    const onSubmit = useCallback(
        (formData: PlantingOperationFormType) => {
            if (!isInitialized || !selectedCrop) return;

            const payload = {
                start_date: dayjs(formData.start_date).format("YYYY-MM-DD"),
                field: fieldId,
                crop_configuration: formData.crop_configuration,
                note: formData.note === "" ? null : formData.note,
                varieties: [
                    {
                        name: formData.hybrid,
                        grower: fieldQuery?.data?.grower,
                        ...(selectedCrop.planting_form_fields.includes("crm")
                            ? {
                                  crm: formData.crm,
                                  gdd_silk: formData.gdd_silk,
                                  gdd_mature: formData.gdd_mature,
                              }
                            : {}),
                        maturity_level: formData.maturity_level,
                        rice_type: formData.rice_type,
                        sugarcane_type: formData.sugarcane_type,
                        soybean_maturity_group: formData.soybean_maturity_group,
                    },
                ],
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any;

            const rates = (formData.table ?? [])?.map((row) => ({
                zone: row.zone_id,
                rate_1: row.seeding_rate,
                rate_2: row.depth,
            }));
            if (
                !selectedFile &&
                !hasFileSource(existingPlantingOperationQuery.data)
            ) {
                const inputRates = existingPlantingOperationQuery.data?.input_rates;
                if (
                    inputRates?.rates?.length &&
                    ["treatment", "treatment_zone", "irrigated"].includes(inputRates.by)
                ) {
                    payload[`${inputRates.by}_rates`] = rates.map((rate, index) => ({
                        ...rate,
                        ...(inputRates.by === "irrigated"
                            ? { irrigated: inputRates.rates[index].irrigated }
                            : { treatment: inputRates.rates[index].treatment }),
                    }));
                } else if (rates.length === 1 && rates[0].zone === null) {
                    payload["field_rate"] = rates[0];
                } else if (rates.length > 0) {
                    payload["zone_rates"] = rates;
                }
            }

            savePlantingOperationMutation.mutate(
                {
                    url: existingPlantingOperationId
                        ? `planting_operations/${existingPlantingOperationId}/`
                        : `planting_operations/`,
                    method: existingPlantingOperationId ? "PUT" : "POST",
                    data: payload,
                },
                {
                    onSuccess: (data) => {
                        if (selectedFile) {
                            const fileUpload = new FormData();
                            fileUpload.append("file", selectedFile);
                            fileUpload.append(
                                "properties",
                                JSON.stringify(getValues("properties")),
                            );
                            uploadOperation.mutate(
                                {
                                    data: fileUpload,
                                    url: `planting_operations/${data.id}/uploadV2/`,
                                    method: "put",
                                },
                                {
                                    onSuccess: () => {
                                        cropCycleQueryV2?.refetch();
                                        navigate({
                                            to: "/dashboard/fields/$fieldId/operations",
                                            params: {
                                                fieldId: fieldId as string,
                                            },
                                        });
                                    },
                                },
                            );
                        } else {
                            cropCycleQueryV2?.refetch();
                            navigate({
                                to: "/dashboard/fields/$fieldId/operations",
                                params: {
                                    fieldId: fieldId as string,
                                },
                            });
                        }
                    },
                },
            );
        },
        [
            existingPlantingOperationId,
            existingPlantingOperationQuery.data,
            fieldId,
            fieldQuery?.data?.grower,
            getValues,
            navigate,
            savePlantingOperationMutation,
            selectedFile,
            uploadOperation,
            cropCycleQueryV2,
            isInitialized,
            selectedCrop,
        ],
    );

    useEffect(() => {
        if (
            !isInitialized &&
            !existingPlantingOperationQuery.isFetching &&
            existingPlantingOperationQuery.isSuccess &&
            existingPlantingOperationQuery.data
        ) {
            setValue(
                "start_date",
                dayjs(existingPlantingOperationQuery.data.start_date).toDate(),
            );
            setValue(
                "crop_configuration",
                // @ts-expect-error - id is a number
                existingPlantingOperationQuery.data.crop_configuration.id.toString() as number,
            );
            setValue(
                "hybrid",
                existingPlantingOperationQuery.data.varieties[0]?.name,
            );
            setValue(
                "crm",
                existingPlantingOperationQuery.data.varieties[0]?.crm,
            );
            setValue(
                "gdd_silk",
                existingPlantingOperationQuery.data.varieties[0]?.gdd_silk,
            );
            setValue(
                "gdd_mature",
                existingPlantingOperationQuery.data.varieties[0]?.gdd_mature,
            );
            setValue(
                "maturity_level",
                existingPlantingOperationQuery.data.varieties[0]
                    ?.maturity_level,
            );
            setValue(
                "rice_type",
                existingPlantingOperationQuery.data.varieties[0]?.rice_type,
            );
            setValue(
                "sugarcane_type",
                existingPlantingOperationQuery.data.varieties[0]
                    ?.sugarcane_type,
            );
            setValue(
                "soybean_maturity_group",
                existingPlantingOperationQuery.data.varieties[0]
                    ?.soybean_maturity_group,
            );
            setValue("note", existingPlantingOperationQuery.data.note);
            setInitializedPlantingOperationId(existingPlantingOperationId);
        }
    }, [
        isInitialized,
        existingPlantingOperationId,
        existingPlantingOperationQuery.data,
        existingPlantingOperationQuery.isFetching,
        existingPlantingOperationQuery.isSuccess,
        setValue,
    ]);

    useEffect(() => {
        if (
            !existingPlantingOperationId ||
            !existingPlantingOperationQuery.data
        ) {
            return;
        }

        const inputRates = existingPlantingOperationQuery.data.input_rates;
        if (hasInputRates(inputRates)) {
            const rows = inputRatesToPlantingTableRows(inputRates!).map(
                (row) => ({
                    ...row,
                    zone:
                        row.zone_id && preferredManagementZones
                            ? (preferredManagementZones.zones.find(
                                (zone) => zone.id === row.zone_id,
                            )?.zone_number ?? null)
                            : row.zone,
                }),
            );
            setValue("table", rows);
            return;
        }

        if (!existingPlantingOperationRatesQuery.data) {
            return;
        }

        if (Array.isArray(existingPlantingOperationRatesQuery.data)) {
            setValue(
                "table",
                (
                    existingPlantingOperationRatesQuery.data as unknown as {
                        [x: string]: number;
                    }[]
                )?.map((rate) => ({
                    zone: rate.zone_number,
                    depth: rate.mean_2,
                    seeding_rate: rate.mean_1 ?? rate.mean,
                    zone_id: rate.id,
                })),
            );
        } else {
            setValue("table", [
                {
                    zone: null,
                    depth: existingPlantingOperationRatesQuery.data.rate_2,
                    seeding_rate:
                        existingPlantingOperationRatesQuery.data.rate_1,
                    zone_id: null,
                },
            ]);
        }
    }, [
        existingPlantingOperationId,
        existingPlantingOperationQuery.data,
        existingPlantingOperationRatesQuery.data,
        preferredManagementZones,
        setValue,
    ]);

    useEffect(() => {
        if (!existingPlantingOperationId) {
            if (preferredManagementZones) {
                setValue(
                    "table",
                    preferredManagementZones.zones.map((zone) => ({
                        zone: zone.zone_number,
                        depth: 0,
                        seeding_rate: 0,
                        zone_id: zone.id,
                    })),
                );
            } else {
                setValue("table", [
                    {
                        zone: null,
                        depth: 0,
                        seeding_rate: 0,
                        zone_id: null,
                    },
                ]);
            }
        }
    }, [
        existingPlantingOperationId,
        existingPlantingOperationRatesQuery.data,
        preferredManagementZones,
        setValue,
    ]);

    const handleBatchFill = useCallback(
        (key: "depth" | "seeding_rate") => {
            const formTable = getValues("table");
            setValue(
                "table",
                formTable?.map((row) => {
                    row[key] = batch_fill[key];
                    return row;
                }),
            );
        },
        [batch_fill, getValues, setValue],
    );

    const wasFileUploadOrOperation = useMemo(
        () => hasFileSource(existingPlantingOperationQuery.data),
        [existingPlantingOperationQuery.data],
    );

    const tableReadOnly =
        !!existingPlantingOperationId && wasFileUploadOrOperation;

    useEffect(() => {
        setValue(
            "_skipTableValidation",
            wasFileUploadOrOperation || !!selectedFile,
        );
    }, [wasFileUploadOrOperation, selectedFile, setValue]);

    if (!isInitialized) {
        return existingPlantingOperationQuery.isError ? (
            <div className="text-sm text-red-500">
                Unable to load planting details.
            </div>
        ) : (
            <Spinner />
        );
    }

    return (
        <FormProvider {...methods}>
            <form>
                <div className="flex flex-col gap-2">
                    <PlantingCropDetailsForm
                        crops={(cropsQuery.data ?? []).map((crop) => ({
                            id: crop.id,
                            description: crop.crop_name,
                        }))}
                        selectedCrop={selectedCrop}
                    />
                    <TextAreaInput
                        className="flex-1"
                        name="note"
                        label={t("note")}
                        variant="flat"
                        size="sm"
                        placeholder={t("note")}
                        labelPlacement="outside"
                    />
                    <Divider />
                    {!wasFileUploadOrOperation && (
                        <ShapefileUpload
                            fileInputProperties={fileProperties.data ?? []}
                            setFile={setSelectedFile}
                        />
                    )}
                    {wasFileUploadOrOperation && (
                        <div className="text-red-500 text-sm italic">
                            This operation was uploaded or synced from a
                            connection
                        </div>
                    )}
                    {!selectedFile && (
                        <>
                            <div className="font-semibold text-lg flex flex-row justify-between items-center">
                                <div className="font-semibold text-lg">
                                    {t("planting") + " " + t("table")}
                                </div>
                                {!tableReadOnly && (
                                    <div className="flex flex-row gap-2">
                                        <TextInput
                                            name="batch_fill.depth"
                                            size="sm"
                                            label={`${t("seedingDepth")} (${formatUnit("in", "cm")})`}
                                            labelPlacement="outside"
                                            type="number"
                                            classNames={{
                                                inputWrapper: "pr-0",
                                            }}
                                            isDisabled={tableReadOnly}
                                            placeholder="0"
                                            endContent={
                                                <Button
                                                    isIconOnly
                                                    as={"div"}
                                                    size="sm"
                                                    color="primary"
                                                    isDisabled={tableReadOnly}
                                                    onPress={() =>
                                                        handleBatchFill("depth")
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faFill}
                                                    />
                                                </Button>
                                            }
                                        />
                                        <TextInput
                                            name="batch_fill.seeding_rate"
                                            size="sm"
                                            label={`${t("seedingRate")} (seeds/${formatUnit("ac", "ha")})`}
                                            labelPlacement="outside"
                                            type="number"
                                            classNames={{
                                                inputWrapper: "pr-0",
                                            }}
                                            isDisabled={tableReadOnly}
                                            placeholder="0"
                                            endContent={
                                                <Button
                                                    isIconOnly
                                                    as={"div"}
                                                    size="sm"
                                                    color="primary"
                                                    isDisabled={tableReadOnly}
                                                    onPress={() =>
                                                        handleBatchFill(
                                                            "seeding_rate",
                                                        )
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faFill}
                                                    />
                                                </Button>
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                            <Table
                                aria-label="Application Table"
                                classNames={{
                                    td: "p-1",
                                }}
                            >
                                <TableHeader columns={COLUMNS}>
                                    {(item) => (
                                        <TableColumn key={item.key}>
                                            {item.label}
                                        </TableColumn>
                                    )}
                                </TableHeader>
                                <TableBody
                                    isLoading={
                                        existingPlantingOperationQuery.isFetching ||
                                        existingPlantingOperationRatesQuery.isFetching ||
                                        managementZonesQuery.isFetching
                                    }
                                    loadingContent={
                                        <Spinner label="Loading..." />
                                    }
                                    emptyContent={
                                        <div className="text-red-500 italic">
                                            No table found, please contact
                                            support
                                        </div>
                                    }
                                >
                                    {fields.map((item, index) => (
                                        <TableRow key={item.id}>
                                            {(columnKey) => {
                                                if (columnKey === "depth") {
                                                    return (
                                                        <TableCell>
                                                            <TextInput
                                                                key={item.id}
                                                                aria-label="Depth"
                                                                size="sm"
                                                                type="number"
                                                                name={`table.${index}.depth`}
                                                                placeholder="Depth"
                                                                decimalPlaces={
                                                                    2
                                                                }
                                                                isDisabled={
                                                                    tableReadOnly
                                                                }
                                                                hideErrorMessage
                                                            />
                                                        </TableCell>
                                                    );
                                                }
                                                if (
                                                    columnKey === "seeding_rate"
                                                ) {
                                                    return (
                                                        <TableCell>
                                                            <TextInput
                                                                key={item.id}
                                                                aria-label="Seeding Rate"
                                                                size="sm"
                                                                type="number"
                                                                name={`table.${index}.seeding_rate`}
                                                                placeholder="Seeding Rate"
                                                                decimalPlaces={
                                                                    2
                                                                }
                                                                isDisabled={
                                                                    tableReadOnly
                                                                }
                                                                hideErrorMessage
                                                            />
                                                        </TableCell>
                                                    );
                                                }
                                                return (
                                                    <TableCell>
                                                        {getKeyValue(
                                                            item,
                                                            columnKey,
                                                        )}
                                                    </TableCell>
                                                );
                                            }}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </>
                    )}

                    <Button
                        fullWidth
                        color="secondary"
                        isDisabled={!selectedCrop}
                        onPress={() => {
                            trigger();
                            handleSubmit(onSubmit)();
                        }}
                        isLoading={
                            savePlantingOperationMutation.isPending ||
                            uploadOperation.isPending
                        }
                    >
                        <FontAwesomeIcon
                            icon={
                                existingPlantingOperationId
                                    ? faSave
                                    : faPlusCircle
                            }
                            className="mr-2"
                        />
                        {existingPlantingOperationId
                            ? t("saveOperation")
                            : t("addOperation")}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
}

export default PlantingOperationForm;
